import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** One `label · value` line. The value is mono, because every one of them is a figure. */
export interface HoverRow {
  label: string;
  value: string;
}

export interface HoverContent {
  title: string;
  rows: HoverRow[];
  /** A closing line under a rule — the design's `Click to open the day in Cards`. Only pass it
   *  where the promise is actually kept: a caption claiming an action that does nothing is worse
   *  than no caption at all. */
  footer?: string;
}

/** Distance from the mark to the card, and from the card to the viewport edge. */
const GAP = 10;

/**
 * The dark hover card every chart on Stats uses (日本-337, Alex 8 Aug 2026).
 *
 * The design draws it once, on a heatmap cell, and it is the same object on a load bar and a
 * coverage segment — so it is one component rather than a `title` attribute per panel. `title` was
 * the original choice here and `Bars` still documents why (free, accessible, unclippable); what it
 * cannot do is carry four labelled figures, and the four figures are what the design asks for.
 *
 * **Portalled and `position: fixed`, where the design is absolute inside the panel.** The heatmap
 * scrolls horizontally inside its own box, so a card positioned within it is clipped by the very
 * container the pointer is in — and a clipped tooltip is invisible while still being present, which
 * is the failure `components/shell/Menu.tsx` already documents for menus. Fixed to the viewport
 * cannot be clipped by an ancestor and needs no knowledge of who is scrolling.
 *
 * **It measures itself before it flips.** The design flips at a hardcoded column (`col * 11 > 360`)
 * because it knows its own frame; here the card's width depends on its content — `585,420` and `12`
 * are different cards — so the choice is made from the real rect after layout, in a
 * `useLayoutEffect` so it is never painted in the wrong place first.
 *
 * `pointer-events: none` is load-bearing rather than tidy: the card is drawn near the mark, and a
 * card that could receive the pointer would take it off the mark, hide itself, and flicker.
 */
function HoverCard({ anchor, content }: { anchor: DOMRect; content: HoverContent }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [placed, setPlaced] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const box = ref.current?.getBoundingClientRect();
    if (!box) return;
    // Right of the mark by default — the reading direction, and what the frame draws. Left when
    // that would run off the viewport, which is the whole right-hand third of a 53-week grid.
    let left = anchor.right + GAP;
    if (left + box.width > window.innerWidth - GAP) left = anchor.left - GAP - box.width;
    left = Math.max(GAP, left);

    // Vertically centred on the mark, then pushed back inside the viewport. A cell is 8px, so
    // centring is what keeps the card pointing at the row you are on rather than at the one above.
    let top = anchor.top + anchor.height / 2 - box.height / 2;
    top = Math.min(Math.max(GAP, top), window.innerHeight - box.height - GAP);
    setPlaced({ left, top });
  }, [anchor, content]);

  return createPortal(
    <div
      ref={ref}
      role="tooltip"
      className="pointer-events-none fixed flex flex-col gap-[6px] rounded-[var(--r-3)] px-[12px] py-[10px]"
      style={{
        background: "var(--chart-tip-bg)",
        boxShadow: "var(--chart-tip-shadow)",
        // Above the surfaces, below a menu: the card is passive and must never sit over something
        // you can click. The ramp is `tokens.css`'s, not a bare number.
        zIndex: "var(--z-chart-hover)",
        left: placed?.left ?? 0,
        top: placed?.top ?? 0,
        // Measured off-screen on the first pass rather than drawn at (0,0) and moved — a card that
        // appears in the corner for one frame is a flicker on every single cell you cross.
        visibility: placed ? "visible" : "hidden",
      }}
    >
      <span
        className="whitespace-nowrap text-[14px]/[1.3] font-semibold"
        style={{ color: "var(--chart-tip-text)" }}
      >
        {content.title}
      </span>
      {content.rows.map((row) => (
        <div key={row.label} className="flex items-baseline gap-[16px]">
          <span
            className="flex-1 whitespace-nowrap text-[14px]/[1.4]"
            style={{ color: "var(--chart-tip-label)" }}
          >
            {row.label}
          </span>
          <span
            className="whitespace-nowrap font-mono text-[14px]/[1.3] font-semibold"
            style={{ color: "var(--chart-tip-text)" }}
          >
            {row.value}
          </span>
        </div>
      ))}
      {content.footer && (
        <span
          className="whitespace-nowrap border-t pt-[5px] text-[14px]/[1.4]"
          style={{ color: "var(--chart-tip-muted)", borderColor: "var(--chart-tip-line)" }}
        >
          {content.footer}
        </span>
      )}
    </div>,
    document.body,
  );
}

/**
 * Hover handling for a panel of marks.
 *
 * Returns the two handlers to spread onto every mark and the card to render once. The **anchor is a
 * rect, not an element**, so nothing here holds a reference to a node that may have been unmounted
 * by the time the card renders.
 *
 * **The hovered mark is not React state.** ~2,560 heatmap cells re-rendering on every pointer move
 * across a grid is the one performance risk this surface actually has; the ring is a `:hover` rule
 * in `index.css` instead, so the browser does it and React only ever re-renders the card.
 */
export function useChartHover() {
  const [state, setState] = useState<{ anchor: DOMRect; content: HoverContent } | null>(null);

  const onEnter = useCallback((event: { currentTarget: Element }, content: HoverContent) => {
    setState({ anchor: event.currentTarget.getBoundingClientRect(), content });
  }, []);
  const onLeave = useCallback(() => setState(null), []);

  // A wheel over a stationary pointer moves the mark and not the card, which would leave it
  // labelling whatever slid underneath — the heatmap scrolls sideways *and* the page scrolls, so
  // both are one gesture away. Capture phase, because the scroll that matters is a container's
  // rather than the window's and container scrolls do not bubble.
  useLayoutEffect(() => {
    if (!state) return;
    const dismiss = () => setState(null);
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => {
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [state]);

  const card: ReactNode = state ? (
    <HoverCard anchor={state.anchor} content={state.content} />
  ) : null;

  return { onEnter, onLeave, card };
}
